/**
 * Everywhere this account is currently signed in.
 *
 * Browser sessions and MCP tokens appear in one list because they are the same thing: a
 * credential that can act as this account, revocable the same way. A page that showed only
 * the browsers would be telling the owner they are signed in fewer places than they are.
 *
 * Every row can be signed out from here, including this one — which is the plain, literal
 * meaning of "log out", available without hunting through a menu.
 */
import { LogOut } from 'lucide-react'
import { Button } from '@/components/ui/Controls'
import { Panel } from '@/components/ui/Panel'
import { QueryBoundary, EmptyState, TableSkeleton } from '@/components/ui/States'
import { useRevokeOtherSessions, useRevokeSession, useSessions } from '@/features/profile/useProfile'
import { formatRelative, formatTimestamp } from '@/lib/utils/format'

/**
 * A user agent string, as something a person recognises.
 *
 * Deliberately shallow: the goal is "was that me, on my phone?", not device fingerprinting.
 * Anything unrecognised keeps its raw string rather than being labelled "Unknown", because
 * the raw string is still evidence and a guess is not.
 */
export function describeAgent(userAgent: string | null | undefined): string {
  if (!userAgent) return 'Unknown device'
  const browser =
    /Edg\//.test(userAgent) ? 'Edge'
    : /OPR\//.test(userAgent) ? 'Opera'
    : /Chrome\//.test(userAgent) ? 'Chrome'
    : /Safari\//.test(userAgent) && /Version\//.test(userAgent) ? 'Safari'
    : /Firefox\//.test(userAgent) ? 'Firefox'
    : null
  const platform =
    /Android/.test(userAgent) ? 'Android'
    : /iPhone|iPad/.test(userAgent) ? 'iOS'
    : /Windows/.test(userAgent) ? 'Windows'
    : /Mac OS X/.test(userAgent) ? 'macOS'
    : /Linux/.test(userAgent) ? 'Linux'
    : null
  if (!browser) return userAgent.slice(0, 60)
  return platform ? `${browser} on ${platform}` : browser
}

export function SessionsPanel() {
  const sessions = useSessions()
  const revoke = useRevokeSession()
  const revokeOthers = useRevokeOtherSessions()

  const rows = sessions.data?.sessions ?? []
  const others = rows.filter((row) => !row.current).length

  return (
    <Panel
      title="Where you are signed in"
      description="Browser sessions and tokens issued to external MCP hosts"
      actions={
        <Button
          size="sm"
          icon={LogOut}
          disabled={others === 0 || revokeOthers.isPending}
          onClick={() => revokeOthers.mutate()}
          title="Revokes every other browser session and MCP token. This one keeps working."
        >
          {revokeOthers.isPending ? 'Signing out…' : 'Sign out everywhere else'}
        </Button>
      }
    >
      <QueryBoundary
        isLoading={sessions.isLoading}
        error={sessions.error}
        onRetry={() => void sessions.refetch()}
        loadingFallback={<TableSkeleton rows={3} columns={4} />}
      >
        {rows.length === 0 ? (
          <EmptyState title="No active sessions" hint="Nothing is signed in to this account." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Session</th>
                  <th scope="col" className="w-28">
                    Kind
                  </th>
                  <th scope="col" className="w-40">
                    Last active
                  </th>
                  <th scope="col" className="w-28 text-right">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="text-ink-dim">
                      <span className="flex items-center gap-2">
                        {row.label ?? describeAgent(row.user_agent)}
                        {row.current ? (
                          <span className="rounded-sm border border-ok/40 bg-ok/10 px-1.5 text-[11px] leading-5 text-ok">
                            this device
                          </span>
                        ) : null}
                      </span>
                      <span className="block text-xs text-ink-faint" title={row.user_agent ?? undefined}>
                        Started {formatTimestamp(row.created_at)}
                      </span>
                    </td>
                    <td className="mono text-xs text-ink-muted">{row.kind}</td>
                    <td className="text-xs text-ink-muted">{formatRelative(row.last_seen_at)}</td>
                    <td className="text-right">
                      <Button
                        size="sm"
                        variant={row.current ? 'default' : 'danger'}
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(row.id)}
                        title={
                          row.current
                            ? 'Signs you out of this browser.'
                            : 'Revokes this credential immediately.'
                        }
                      >
                        {row.current ? 'Sign out' : 'Revoke'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </Panel>
  )
}
